import { useEffect, useState } from "react";
import shortid from "shortid";
import {
  Button,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  Box,
  Grid,
  TextField,
  CurrencyInput,
  Paper,
  DataGrid,
  Loading,
} from "../../../../components";
import { formatPrice, responseErros, unformatPrice } from "../../../../utils";
import {
  equipamentoListValidator,
  equipamentoValidator,
} from "../middlewares/equipamento-servico.validator";
import { InputErros } from "../../../../helpers";
import { EquipamentosService, notification } from "../../../../services";

export function EquipamentoServicoDialog({ open, setOpen }) {
  const [equipamentos, setEquipamentos] = useState([]);
  const [equipamento, setEquipamento] = useState({});
  const [isItemEdit, setIsItemEdit] = useState(false);
  const [idsRemoved, setIdsRemoved] = useState([]);
  const [loading, setLoading] = useState(false);
  const equipamentoService = new EquipamentosService();

  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  useEffect(() => {
    if (open) {
      buscarEquipamentos();
    }
  }, [open]);

  async function buscarEquipamentos() {
    setLoading(true);
    const result = await equipamentoService.getAll();
    if (!result.isAxiosError) {
      setEquipamentos(result.data.rows);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  const columns = [
    { field: "descricao", headerName: "Descrição", flex: 250 },
    {
      field: "valorAquisicao",
      headerName: "Valor Aquisição",
      flex: 80,
      valueFormatter: (params) => `R$ ${formatPrice(params.value || 0, false)}`,
    },
    { field: "vidaUtil", headerName: "Vida Útil", flex: 80 },
    {
      field: "manutencaoMensal",
      headerName: "Manutenção Mensal",
      flex: 80,
      valueFormatter: (params) => `R$ ${formatPrice(params.value || 0, false)}`,
    },
    { field: "horasUsoMes", headerName: "Horas Uso/Mês", flex: 80 },
    {
      field: "custoHora",
      headerName: "Custo/Hora",
      flex: 80,
      valueFormatter: (params) => `R$ ${formatPrice(params.value || 0, false)}`,
    },
  ];

  function onClose() {
    setEquipamento({});
    setEquipamentos([]);
    setIdsRemoved([]);
    setIsItemEdit(false);
    setOpen(false);
  }

  function adicionar(item = equipamento) {
    const equipamentoTemp = {
      id: item?.itemEditado ? item.id : shortid.generate(),
      descricao: item.descricao,
      valorAquisicao: parseFloat(item.valorAquisicao) || 0,
      vidaUtil: parseFloat(item.vidaUtil) || 1,
      manutencaoMensal: parseFloat(item.manutencaoMensal) || 0,
      horasUsoMes: parseFloat(item.horasUsoMes) || 1,
      depreciacao: parseFloat(item.depreciacao) || 0,
      custoMensal: parseFloat(item.custoMensal) || 0,
      custoHora: parseFloat(item.custoHora) || 0,
    };

    equipamentoValidator
      .validate(equipamentoTemp, { abortEarly: false })
      .then(() => {
        if (item?.itemEditado) {
          setEquipamentos((prev) =>
            prev.map((m) => (m.id === item.id ? equipamentoTemp : m)),
          );
          setIsItemEdit(true);
        } else {
          setEquipamentos((prev) => [equipamentoTemp, ...prev]);
        }
        setEquipamento({});
        inputErros.limpar();
      })
      .catch((err) => {
        inputErros.set(err);
      });
  }

  async function upsertEquipamentos() {
    equipamentoListValidator
      .validate(equipamentos, { abortEarly: false })
      .then(async () => {
        setLoading(true);
        const body = { items: equipamentos, idsRemoved };
        const result = await equipamentoService.upsert(body);
        if (!result.isAxiosError) {
          notification.alteracaoSucesso();
          onClose();
        } else {
          responseErros(result);
        }
        setLoading(false);
      })
      .catch((err) => {
        inputErros.set(err);
      });
  }

  function onClickEditEquipamento(id) {
    const edit = equipamentos.find((m) => m.id === id);
    if (edit) {
      setEquipamento({ ...edit, itemEditado: true });
    }
  }

  function onClickDeleteEquipamento(id) {
    const removed = equipamentos.find((m) => m.id === id);
    if (removed && typeof removed.id === "number") {
      setIdsRemoved((prev) => [...prev, removed.id]);
    }
    setEquipamentos((prev) => prev.filter((m) => m.id !== id));
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperComponent="div"
      PaperProps={{ style: { borderRadius: "0.7rem", background: "#ffffff" } }}
    >
      <DialogContent>
        <CardHeader title="Novo Equipamento" sx={{ padding: "1rem 1.6rem" }} />

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                id="descricao"
                name="descricao"
                label="Descrição"
                required
                margin="normal"
                value={equipamento.descricao || ""}
                onChange={(e) =>
                  setEquipamento({ ...equipamento, descricao: e.target.value })
                }
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <CurrencyInput
                label="Valor Aquisição"
                value={equipamento.valorAquisicao || 0}
                onChange={(e) => {
                  const value = unformatPrice(e.target.value);
                  const depreciacao = value / (equipamento.vidaUtil || 1);
                  setEquipamento({
                    ...equipamento,
                    depreciacao,
                    valorAquisicao: unformatPrice(e.target.value),
                  });
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label="Vida Útil"
                type="number"
                value={equipamento.vidaUtil || 1}
                margin="normal"
                onChange={(e) => {
                  const vidaUtil = e.target.value;
                  const depreciacao =
                    (equipamento.valorAquisicao || 0) / (vidaUtil || 1);
                  setEquipamento({ ...equipamento, vidaUtil, depreciacao });
                }}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <CurrencyInput
                label="Depreciação"
                value={equipamento.depreciacao || 0}
                onChange={(e) => {
                  const depreciacao = unformatPrice(e.target.value);
                  const custoMensal =
                    depreciacao + (equipamento.manutencaoMensal || 0);
                  setEquipamento({
                    ...equipamento,
                    depreciacao,
                    custoMensal,
                  });
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <CurrencyInput
                label="Manutenção Mensal"
                value={equipamento.manutencaoMensal || 0}
                onChange={(e) => {
                  const manutencaoMensal = unformatPrice(e.target.value);
                  const custoMensal =
                    (parseFloat(equipamento.depreciacao) || 0) +
                    parseFloat(manutencaoMensal);
                  setEquipamento({
                    ...equipamento,
                    manutencaoMensal,
                    custoMensal,
                  });
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label="Horas Uso/Mês"
                type="number"
                value={equipamento.horasUsoMes || 1}
                margin="normal"
                onChange={(e) => {
                  const horasUsoMes = e.target.value;
                  const custoHora =
                    (equipamento.custoMensal || 0) / (horasUsoMes || 1);
                  setEquipamento({
                    ...equipamento,
                    horasUsoMes,
                    custoHora,
                  });
                }}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <CurrencyInput
                label="Custo Mensal"
                value={equipamento.custoMensal || 0}
                onChange={(e) => {
                  const custoMensal = unformatPrice(e.target.value);
                  const custoHora =
                    custoMensal / (equipamento.horasUsoMes || 1);
                  setEquipamento({
                    ...equipamento,
                    custoMensal,
                    custoHora,
                  });
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <CurrencyInput
                label="Custo/Hora"
                value={equipamento.custoHora || 0}
                onChange={(e) =>
                  setEquipamento({
                    ...equipamento,
                    custoHora: unformatPrice(e.target.value),
                  })
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => adicionar(equipamento)}
              >
                {equipamento.itemEditado ? "Atualizar" : "Adicionar"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
        <Box>
          <DataGrid
            rows={equipamentos}
            columns={columns}
            onClickEdit={onClickEditEquipamento}
            onClickDelete={onClickDeleteEquipamento}
            editShow
            deleteShow
          />
        </Box>
      </DialogContent>
      <DialogActions className="me-2">
        <Button
          onClick={onClose}
          color="padrao"
          variant="contained"
          disableElevation
        >
          Fechar
        </Button>
        {((equipamentos.length > 0 &&
          equipamentos.some((m) => typeof m.id === "string")) ||
          isItemEdit ||
          idsRemoved?.length > 0) && (
          <Button
            onClick={upsertEquipamentos}
            color="primary"
            variant="contained"
            disableElevation
          >
            Salvar
          </Button>
        )}
      </DialogActions>
      <Loading loading={loading} />
    </Dialog>
  );
}
